import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import './index.scss';

// ─── Mock Data per variant ───────────────────────────────────────
var MOCK_PRESETS = {
    number: { value: 125000000, format: 'currency', trend: { value: 12.5, direction: 'up' }, icon: '💰', title: { vi: 'Doanh thu', en: 'Revenue', fr: 'Revenus', es: 'Ingresos', id: 'Pendapatan', ja: '売上', zh: '营收', ko: '매출', hi: 'राजस्व', ar: 'الإيرادات' } },
    progress: { value: 75, target: 100, format: 'number', unit: { vi: 'đơn hàng', en: 'orders', fr: 'commandes', es: 'pedidos', id: 'pesanan', ja: '件', zh: '单', ko: '건', hi: 'ऑर्डर', ar: 'طلبات' }, showPercentage: true, title: { vi: 'Đơn hàng', en: 'Orders', fr: 'Commandes', es: 'Pedidos', id: 'Pesanan', ja: '注文', zh: '订单', ko: '주문', hi: 'ऑर्डर', ar: 'الطلبات' } },
    circle: { value: 72.5, maxValue: 100, format: 'percent', showPercentage: true, color: '#3182CE', title: { vi: 'Sprint Q1', en: 'Sprint Q1' } },
    comparison: { current: 500000000, previous: 420000000, format: 'currency', title: { vi: 'So sánh tháng', en: 'Monthly Comparison', fr: 'Comparaison mensuelle', es: 'Comparación mensual', id: 'Perbandingan bulanan', ja: '月次比較', zh: '月度对比', ko: '월별 비교', hi: 'मासिक तुलना', ar: 'مقارنة شهرية' } },
    sparkline: { value: 45, sparklineData: [30, 35, 28, 42, 38, 50, 45], format: 'number', trend: { value: 8.3, direction: 'up' }, title: { vi: 'Đơn hàng 7 ngày', en: '7-day Orders', fr: 'Commandes 7 jours', es: 'Pedidos 7 días', id: 'Pesanan 7 hari', ja: '7日間の注文', zh: '7 天订单', ko: '7일 주문', hi: '7 दिन के ऑर्डर', ar: 'طلبات 7 أيام' } },
    multi: {
        metrics: [
            { label: { vi: 'Cần làm', en: 'Todo', fr: 'À faire', es: 'Por hacer', id: 'Tertunda', ja: '未着手', zh: '待办', ko: '할 일', hi: 'करना है', ar: 'للتنفيذ' }, value: 12, color: '#718096' },
            { label: { vi: 'Đang làm', en: 'WIP', fr: 'En cours', es: 'En curso', id: 'Dikerjakan', ja: '進行中', zh: '进行中', ko: '진행 중', hi: 'प्रगति में', ar: 'قيد التنفيذ' }, value: 8, color: '#3182CE' },
            { label: { vi: 'Hoàn thành', en: 'Done', fr: 'Terminé', es: 'Completado', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' }, value: 25, color: '#38A169' },
            { label: { vi: 'Quá hạn', en: 'Overdue', fr: 'En retard', es: 'Vencidas', id: 'Terlambat', ja: '期限超過', zh: '已逾期', ko: '기한 초과', hi: 'अतिदेय', ar: 'متأخرة' }, value: 3, color: '#E53E3E' }
        ],
        title: { vi: 'Tổng quan task', en: 'Task Overview', fr: 'Aperçu des tâches', es: 'Resumen de tareas', id: 'Ringkasan tugas', ja: 'タスク概要', zh: '任务概览', ko: '작업 개요', hi: 'कार्य सारांश', ar: 'نظرة على المهام' }
    }
};

const formatValue = (value, format, lang) => {
    if (value === null || value === undefined) return '--';
    const num = Number(value);
    if (isNaN(num)) return value;

    if (format === 'percent') {
        return num.toFixed(1) + '%';
    }

    if (format === 'currency') {
        if (lang === 'vi') {
            if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + ' tỷ';
            if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + ' triệu';
            if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(1) + 'K';
            return num.toLocaleString('vi-VN') + 'đ';
        }
        if (Math.abs(num) >= 1e9) return '$' + (num / 1e9).toFixed(1) + 'B';
        if (Math.abs(num) >= 1e6) return '$' + (num / 1e6).toFixed(1) + 'M';
        if (Math.abs(num) >= 1e3) return '$' + (num / 1e3).toFixed(1) + 'K';
        return '$' + num.toLocaleString('en-US');
    }

    // number
    if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toLocaleString();
};

const TrendIndicator = ({ trend }) => {
    if (!trend) return null;
    const isUp = trend.direction === 'up';
    const cls = isUp ? 'trend-up' : 'trend-down';
    return (
        <span className={`trend-indicator ${cls}`}>
            <svg width="12" height="12" viewBox="0 0 12 12">
                {isUp
                    ? <path d="M6 2L10 7H2L6 2Z" fill="currentColor" />
                    : <path d="M6 10L2 5H10L6 10Z" fill="currentColor" />
                }
            </svg>
            <span className="trend-value">{trend.value}%</span>
        </span>
    );
};

const NumberVariant = ({ value, format, trend, unit, lang }) => (
    <div className="variant-number">
        <span className="main-value">{formatValue(value, format, lang)}</span>
        {unit && <span className="unit">{AntLocalization.translateText(unit)}</span>}
        <TrendIndicator trend={trend} />
    </div>
);

const ProgressVariant = ({ value, target, format, trend, showPercentage, lang }) => {
    const pct = target ? Math.min((value / target) * 100, 100) : 0;
    return (
        <div className="variant-progress">
            <div className="progress-header">
                <span className="main-value">{formatValue(value, format, lang)}</span>
                {target != null && <span className="target-value">/ {formatValue(target, format, lang)}</span>}
                <TrendIndicator trend={trend} />
            </div>
            <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: pct + '%' }} />
            </div>
            {showPercentage && <span className="progress-pct">{pct.toFixed(1)}%</span>}
        </div>
    );
};

const CircleVariant = ({ value, maxValue, format, size = 80, thickness = 6, color, showPercentage, lang }) => {
    const pct = maxValue ? Math.min((value / maxValue) * 100, 100) : 0;
    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;
    const center = size / 2;

    return (
        <div className="variant-circle">
            <svg className="circular-progress" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle className="circle-bg" cx={center} cy={center} r={radius}
                    strokeWidth={thickness} fill="none" />
                <circle className="circle-fill" cx={center} cy={center} r={radius}
                    strokeWidth={thickness} fill="none"
                    strokeDasharray={circumference} strokeDashoffset={offset}
                    strokeLinecap="round" style={color ? { stroke: color } : {}}
                    transform={`rotate(-90 ${center} ${center})`} />
            </svg>
            <div className="circle-center-text">
                {showPercentage
                    ? <span className="circle-pct">{Math.round(pct)}%</span>
                    : <span className="circle-value">{formatValue(value, format, lang)}</span>
                }
            </div>
        </div>
    );
};

const ComparisonVariant = ({ current, previous, format, trend, lang }) => {
    const diff = current != null && previous != null ? current - previous : null;
    return (
        <div className="variant-comparison">
            <div className="comparison-row current">
                <span className="comparison-label">{AntLocalization.translateText({ vi: 'Hiện tại', en: 'Current', fr: 'Actuel', es: 'Actual', id: 'Saat ini', ja: '現在', zh: '当前', ko: '현재', hi: 'वर्तमान', ar: 'الحالي' })}</span>
                <span className="comparison-value">{formatValue(current, format, lang)}</span>
            </div>
            <div className="comparison-row previous">
                <span className="comparison-label">{AntLocalization.translateText({ vi: 'Trước đó', en: 'Previous', fr: 'Précédent', es: 'Anterior', id: 'Sebelumnya', ja: '前回', zh: '之前', ko: '이전', hi: 'पिछला', ar: 'السابق' })}</span>
                <span className="comparison-value">{formatValue(previous, format, lang)}</span>
            </div>
            {diff !== null && (
                <div className="comparison-diff">
                    <TrendIndicator trend={trend || { value: previous ? Math.abs((diff / previous) * 100).toFixed(1) : 0, direction: diff >= 0 ? 'up' : 'down' }} />
                </div>
            )}
        </div>
    );
};

const SparklineVariant = ({ sparklineData, value, format, trend, lang }) => {
    if (!sparklineData || !sparklineData.length) return null;
    const max = Math.max(...sparklineData);
    const min = Math.min(...sparklineData);
    const range = max - min || 1;
    const w = 120;
    const h = 36;
    const points = sparklineData.map((v, i) => {
        const x = (i / (sparklineData.length - 1)) * w;
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return `${x},${y}`;
    }).join(' ');

    return (
        <div className="variant-sparkline">
            <div className="sparkline-header">
                <span className="main-value">{formatValue(value, format, lang)}</span>
                <TrendIndicator trend={trend} />
            </div>
            <svg className="sparkline-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
                <polyline className="sparkline-line" points={points}
                    fill="none" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
        </div>
    );
};

const MultiVariant = ({ metrics, format, lang }) => {
    if (!metrics || !metrics.length) return null;
    return (
        <div className="variant-multi">
            {metrics.map((m, i) => (
                <div className="multi-metric" key={i}>
                    <span className="multi-label">{AntLocalization.translateText(m.label)}</span>
                    <span className="multi-value" style={m.color ? { color: m.color } : {}}>
                        {formatValue(m.value, m.format || format, lang)}
                    </span>
                    <TrendIndicator trend={m.trend} />
                </div>
            ))}
        </div>
    );
};

export default function AntProgressCard(props) {
    var {
        variant = 'number', title: titleProp, value: valueProp, target: targetProp, maxValue: maxValueProp,
        format: formatProp = 'number', color: colorProp, icon: iconProp, trend: trendProp,
        current: currentProp, previous: previousProp, sparklineData: sparklineProp, metrics: metricsProp,
        size, thickness, unit: unitProp, showPercentage: showPercentageProp,
        onClick,
        // Mock mode
        mockMode = false,
        // API data source
        API, apiMethod, apiParams,
        // Visibility condition
        visible, session
    } = props;

    var [apiData, setApiData] = useState(null);

    // ─── Visibility check ───────────────────────────────────────
    if (visible && session) {
        var isVis = AntCondition.checkAll({}, visible, session);
        if (!isVis) return null;
    }

    // ─── Load from API ──────────────────────────────────────────
    var loadApiData = useCallback(async function () {
        if (!API || !apiMethod || mockMode) return;
        try {
            var result = await API[apiMethod](apiParams || {});
            if (result && result.data) setApiData(result.data);
            else if (result) setApiData(result);
        } catch (e) { /* silent */ }
    }, [API, apiMethod, apiParams, mockMode]);

    useEffect(function () {
        loadApiData();
    }, [loadApiData]);

    // ─── Resolve: mockMode → API → props ────────────────────────
    var mock = mockMode ? (MOCK_PRESETS[variant] || MOCK_PRESETS.number) : null;
    var resolved = apiData || mock || {};
    var title = titleProp || resolved.title;
    var value = valueProp !== undefined ? valueProp : resolved.value;
    var target = targetProp !== undefined ? targetProp : resolved.target;
    var maxValue = maxValueProp !== undefined ? maxValueProp : resolved.maxValue;
    var format = formatProp || resolved.format || 'number';
    var color = colorProp || resolved.color;
    var icon = iconProp || resolved.icon;
    var trend = trendProp || resolved.trend;
    var current = currentProp !== undefined ? currentProp : resolved.current;
    var previous = previousProp !== undefined ? previousProp : resolved.previous;
    var sparklineData = sparklineProp || resolved.sparklineData;
    var metrics = metricsProp || resolved.metrics;
    var unit = unitProp || resolved.unit;
    var showPercentage = showPercentageProp !== undefined ? showPercentageProp : resolved.showPercentage;

    const lang = useMemo(() => {
        try { return AntLocalization.getLanguage() || 'vi'; } catch (e) { return 'vi'; }
    }, []);

    const titleText = useMemo(() => title ? AntLocalization.translateText(title) : '', [title]);

    const renderVariant = () => {
        switch (variant) {
            case 'progress':
                return <ProgressVariant value={value} target={target} format={format} trend={trend} showPercentage={showPercentage} lang={lang} />;
            case 'circle':
                return <CircleVariant value={value} maxValue={maxValue || target} format={format} size={size} thickness={thickness} color={color} showPercentage={showPercentage} lang={lang} />;
            case 'comparison':
                return <ComparisonVariant current={current} previous={previous} format={format} trend={trend} lang={lang} />;
            case 'sparkline':
                return <SparklineVariant sparklineData={sparklineData} value={value} format={format} trend={trend} lang={lang} />;
            case 'multi':
                return <MultiVariant metrics={metrics} format={format} lang={lang} />;
            default:
                return <NumberVariant value={value} format={format} trend={trend} unit={unit} lang={lang} />;
        }
    };

    return (
        <div className={`ant-progress-card variant-${variant}${onClick ? ' clickable' : ''}`}
            style={color && variant !== 'circle' ? { borderLeftColor: color } : {}}
            onClick={onClick}>
            <div className="card-header">
                {icon && <span className="card-icon">{icon}</span>}
                {titleText && <span className="card-title">{titleText}</span>}
            </div>
            <div className="card-body">
                {renderVariant()}
            </div>
        </div>
    );
}
